// import XIcon from "@mui/icons-material/Close"
import { Component } from "react"
import {
  GoogleMap,
  OverlayView,
  OverlayViewF,
  InfoWindow,
  Marker,
  Circle,
} from "@react-google-maps/api"
import { Box, Button } from "@mui/material"
import { mapStyles } from "@/components/mapBox/mapStyles"
import centerLocationIcon from "@/components/mapBox/images/centerLocation.svg"
import { MarkerData } from "@/common/types"
import ReactGA from "react-ga4"

import { Location } from "react-router-dom"

import InfoWindowDesign from "./InfoWindowDesign"

interface MapState {
  isMobile: boolean
  map: google.maps.Map | null // store the map instance
  center: google.maps.LatLngLiteral
  zoom: number
  coordsResult: google.maps.places.PlaceResult[]
  selectedPlace: google.maps.places.PlaceResult | null
  activeMarker: MarkerData | null
  userLocation: google.maps.LatLngLiteral | null // added for user location
  currentLocationIcon: google.maps.Symbol
  hoveredMarkerId: string | number | null
}

const color = {
  "google-blue 100": `#4285F4`,
  "white 100": `rgb(255,255,255)`,
}

class GoogleMapComponent extends Component<
  {
    markerData: MarkerData[]
    location: Location
    setBounds: React.Dispatch<
      React.SetStateAction<{
        north: number
        south: number
        east: number
        west: number
      }>
    >
    activeFilters?: string[]
  },
  MapState
> {
  constructor(props: {
    markerData: MarkerData[]
    location: Location
    setBounds: React.Dispatch<
      React.SetStateAction<{
        north: number
        south: number
        east: number
        west: number
      }>
    >
    activeFilters?: string[]

  }) {
    super(props)

    const storedCenter = sessionStorage.getItem("mapCenter")
    const storedZoom = sessionStorage.getItem("mapZoom")

    this.state = {
      isMobile: window.innerWidth < 768,
      center: storedCenter ? JSON.parse(storedCenter) :
      { lat: 32.776474, lng: -79.931053 },
      zoom: storedZoom ? Number(storedZoom) : 12,
      coordsResult: [],
      selectedPlace: null,
      activeMarker: null,
      map: null,
      userLocation: null, // initialize userLocation as null
      currentLocationIcon: {
        fillColor: color["google-blue 100"],
        fillOpacity: 1,
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        strokeColor: color["white 100"],
        strokeWeight: 2,
      },
      hoveredMarkerId: null, // or hoveredMarkerId if you prefer
    }
  }

  private hasRestoredMarker = false
  private markerClicked = false
  private shouldPanToUserOnce = false

  restoreActiveMarkerFromStorage = () => {
      if (this.hasRestoredMarker) return

      const storedId = sessionStorage.getItem("activeMarkerId")
      if (!storedId) return

      const foundMarker = this.props.markerData.find(
        (marker) => marker.id === Number(storedId)
      )

      if (foundMarker) {
        this.setState({ activeMarker: foundMarker })

        const { map } = this.state
        if (map) {
          map.panTo(foundMarker.position)
        }
      }

      this.hasRestoredMarker = true
    }

  onMapLoad = (map: google.maps.Map) => {
    this.setState({ map }) // Store the map instance in state

    map.panTo(this.state.center) // center map

    // Delay setting markerClicked to false to avoid race condition
    map.addListener("click", () => {
      if (!this.markerClicked) {
        this.setState({ activeMarker: null })
        sessionStorage.removeItem("activeMarkerId")
      }
      this.markerClicked = false // reset for next interaction
    })


    map.addListener("idle", () => {
      const bounds = map.getBounds()
      if (!bounds) return

      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const queryBounds = {
        north: ne.lat(),
        east: ne.lng(),
        south: sw.lat(),
        west: sw.lng(),
      }

      this.props.setBounds(queryBounds)
    
//changes center/zoom every time the map settles
//when leaving and going back, display same values when you left map
    const center = map.getCenter()
    if (center) {
      sessionStorage.setItem(
        "mapCenter",
        JSON.stringify({ lat: center.lat(), lng: center.lng()})
      )
    }

    const zoom = map.getZoom()
    if (zoom !== undefined){
      sessionStorage.setItem("mapZoom", String(zoom))
    }
  })}

  componentDidMount() {
    const hasCenteredOnUser = sessionStorage.getItem("mapHasCenteredOnUser")
    // get current location
    navigator.geolocation.getCurrentPosition((position) => {
      const userLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      }
      if (!hasCenteredOnUser) {
        //First timne show user at center of map
      this.shouldPanToUserOnce = true
      this.setState({ userLocation, center: userLocation })
      sessionStorage.setItem("mapHasCenteredOnUser", "true")
      } else {
        //returning to map backdrop shows blue location dot but does not center map on user
        //instead it will show the last map center and zoom level stored in session storage
        this.setState({ userLocation })
      }
      this.setActiveMarkerFromUrl()
    })

    document.addEventListener("click", this.handleOutsideClick)

    if (this.props.markerData.length > 0){
      this.restoreActiveMarkerFromStorage()
    }
  }

  componentDidUpdate(
    prevProps: Readonly<{ markerData: MarkerData[]; location: Location; activeFilters?: string[] }>,
    prevState: Readonly<MapState>
  ) {
    if (
      this.shouldPanToUserOnce &&
      this.state.map &&
      this.state.userLocation &&
      this.state.userLocation !== prevState.userLocation
    ) {
      this.state.map.panTo(this.state.userLocation)
      this.shouldPanToUserOnce = false
    }

    if (prevProps.markerData.length === 0 && this.props.markerData.length > 0){
      this.restoreActiveMarkerFromStorage()
    }
  }

  setActiveMarkerFromUrl = () => {
    const searchParams = new URLSearchParams(this.props.location?.search)
    const eventId = searchParams.get("eventId")

    if (eventId) {
      const foundMarker = this.props.markerData.find(
        (marker) => marker.id === Number(eventId)
      )
      if (foundMarker) {
        this.setState({ activeMarker: foundMarker })

        // Optionally center the map on the active marker
        if (this.state.map) {
          this.state.map.panTo(foundMarker.position)
        }
      }
    }
  }

  handleOutsideClick = (event: MouseEvent) => {
    const clickedInsideMap = (event.target as HTMLElement).closest(
      ".google-map-container"
    )
    const clickedInsideDialog = (event.target as HTMLElement).closest(
      ".large-event-dialog" // or whatever class your dialog uses
    )

    if (!clickedInsideMap && !clickedInsideDialog) {
      this.setState({ activeMarker: null })
      sessionStorage.removeItem("activeMarkerId")
    }
  }

  handleMarkerClick = (marker: MarkerData) => {
    this.markerClicked = true // set the flag to true when a marker is clicked


    const { activeMarker } = this.state

    ReactGA.event({
      category: "Map Event",
      action: "Click marker",
      label: `${marker.id}`, // adjust depending on where you store user info
      value: marker.id,
    })

    // If the same marker is already active, close it (toggle off)
    if (activeMarker && activeMarker.id === marker.id) {
      this.setState({ activeMarker: null })
      sessionStorage.removeItem("activeMarkerId")
    } else {
      this.setState({ activeMarker: marker })
      sessionStorage.setItem("activeMarkerId", String(marker.id))
    }
  }

  handleZoomIn = () => {
    const { map } = this.state
    if (map) {
      map.setZoom(map.getZoom()! + 1)
    }
  }

  handleZoomOut = () => {
    const { map } = this.state
    if (map) {
      map.setZoom(map.getZoom()! - 1)
    }
  }

  panToUserLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        this.setState({ userLocation: { lat: latitude, lng: longitude } })

        const { map } = this.state
        if (map) {
          map.panTo({ lat: latitude, lng: longitude })
        }
      },
      (error) => {
        console.error("Error getting user location", error)
      }
    )
  }

  setActiveMarkerToNull = () => {
    this.setState({ activeMarker: null })
    sessionStorage.removeItem("activeMarkerId")
  }

  /**
   * Get the event type to display for a marker based on active filters.
   * If filters are active, returns the first event type that matches a filter.
   * Otherwise, returns the first event type.
   */
  getDisplayedEventType = (marker: MarkerData) => {
    const { activeFilters } = this.props
    const eventTypes = marker.event_types

    if (!eventTypes?.length) return null

    // If no filters are active, return the first event type
    if (!activeFilters?.length) return eventTypes[0]

    // Find the first event type that matches an active filter
    const matchingType = eventTypes.find((type) =>
      activeFilters.includes(type.machine_name)
    )

    // Return the matching type, or fall back to the first type
    return matchingType || eventTypes[0]
  }

  handleMouseEnter = (marker: MarkerData) => {
    if (!this.state.hoveredMarkerId) {
      this.setState({ hoveredMarkerId: Number(marker.id) })
    }
  }

  handleMouseLeave = (marker: MarkerData) => {
    if (this.state.hoveredMarkerId === marker.id) {
      this.setState({ hoveredMarkerId: null })
    }
  }

  render() {
    return (
      <>
        <GoogleMap
          mapContainerClassName="google-map-container"
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={this.state.center}
          zoom={this.state.zoom}
          onLoad={this.onMapLoad}
          options={{
            gestureHandling: "greedy",
            styles: mapStyles,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
            disableDefaultUI: true,
            clickableIcons: false,
          }}
        >
          {/** render marker for user location */}
          {this.state.userLocation && (
            <>
              <Marker
                position={this.state.userLocation}
                options={{ optimized: false }}
                icon={this.state.currentLocationIcon}
              />
              <Circle
                center={this.state.userLocation} // Center of the circle
                radius={500} // default radius if map is null
                options={{
                  fillColor: color["google-blue 100"],
                  fillOpacity: 0.2, // Adjust opacity as needed
                  strokeColor: color["google-blue 100"],
                  strokeOpacity: 0.5, // Adjust opacity as needed
                  strokeWeight: 1,
                }}
              />
            </>
          )}
          {/** render all other markers */}
          {this.props.markerData.slice(0, 20).map((marker, index) => {
            const selectedMarker = this.state.activeMarker
            const largeEventLimit = this.state.isMobile ? 9 : 11
            const isFirstTen = index <= largeEventLimit

            const isHovered = this.state.hoveredMarkerId === marker.id
            const shouldShowImage = isFirstTen || isHovered

            return (
              <OverlayViewF
                key={index}
                position={marker.position}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(width, height) => ({
                  x: -(width / 5.25),
                  y: -(height / 5.25),
                })}
              >
                <Box
                  role="button"
                  onClick={() => this.handleMarkerClick(marker)}
                  onMouseEnter={() => this.handleMouseEnter(marker)}
                  onMouseLeave={() => this.handleMouseLeave(marker)}
                  sx={{
                    position: "relative",
                    zIndex:
                      isHovered || selectedMarker?.id === marker.id
                        ? 10
                        : isFirstTen
                        ? 5
                        : 1,
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.25)",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      paddingX: isFirstTen ? "1rem" : "0.5rem",
                      borderRadius: "16px",
                      backgroundColor:
                        selectedMarker?.id === marker.id ? "#292d6c" : "#FFF",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.18)",
                      position: "relative",
                      transition:
                        "transform 0.2s ease, background-color 0.2s ease",
                      transform: "scale(1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: isFirstTen || isHovered ? "100%" : "14px",
                      width: isFirstTen || isHovered ? "auto" : "16px",
                      maxWidth: "56px",
                      "&:hover": {
                        transform: "scale(1.25)",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                        "& .marker-icon": {
                          display: "block",
                        },
                      },
                    }}
                  >
                    {shouldShowImage && (() => {
                      const displayedType = this.getDisplayedEventType(marker)
                      return displayedType ? (
                        <Box
                          component="img"
                          className="marker-icon"
                          alt={`${displayedType.human_name} in Charleston`}
                          src={displayedType.icon_link}
                          sx={{
                            height: "20px",
                            width: "20px",
                            display: isFirstTen || isHovered ? "block" : "none",
                            filter:
                              selectedMarker?.id === marker.id
                                ? "brightness(0) saturate(100%) invert(1)"
                                : "none",
                          }}
                        />
                      ) : null
                    })()}
                  </Box>
                </Box>
              </OverlayViewF>
            )
          })}

          {/** current active marker */}
          {this.state.activeMarker && (
            // <>
            <InfoWindow
              options={{
                pixelOffset: new window.google.maps.Size(+220, +130),

                // maxWidth: 275,
              }}
              position={this.state.activeMarker.position}
              onCloseClick={() => this.setState({ activeMarker: null })}
            >
              <InfoWindowDesign
                activeMarker={this.state.activeMarker}
                closeInfoWindow={this.setActiveMarkerToNull}
              />
            </InfoWindow>
            // </>
          )}
          {!this.state.isMobile && (
            <Box
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                width: "60px",
                position: "absolute",
                bottom: 23,
                right: 8,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  borderRadius: "0",
                  minWidth: "10px",
                  color: "text.primary",
                  "&:hover": {
                    color: "white",
                  },
                }}
                onClick={this.handleZoomIn}
              >
                +
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  color: "text.primary",
                  borderTop: "1px solid #CCCCCC",
                  borderBottom: "1px solid #CCCCCC",
                  borderRadius: "0",
                  minWidth: "10px",
                  "&:hover": {
                    color: "white",
                  },
                }}
                onClick={this.handleZoomOut}
              >
                -
              </Button>
              <Button
                variant="contained"
                onClick={this.panToUserLocation}
                sx={{
                  "&:hover": {
                    color: "white",
                  },
                  backgroundColor: "white",
                  borderRadius: "0",
                  minWidth: "10px",
                }}
              >
                <img src={centerLocationIcon} alt="center location" />
              </Button>
            </Box>
          )}
        </GoogleMap>
      </>
    )
  }
}

export default GoogleMapComponent
